class CollectionGridVariantPagination {
  constructor(grid) {
    this.grid = grid;
    this.itemsPerPage = Number(grid.dataset.itemsPerPage) || 24;
    this.sourcePages = Number(grid.dataset.sourcePages) || 1;
    this.paginationType = grid.dataset.paginationType || 'paginated';
    this.paginationContainer = grid.parentElement.querySelector('[data-grid-pagination]');
    this.itemPage = Math.max(1, Number(new URL(window.location.href).searchParams.get('items_page')) || 1);
    this.enforceTimer = null;
    this.initialize();
  }

  async initialize() {
    this.grid.classList.add('loading');
    const pages = [Array.from(this.grid.querySelectorAll(':scope > [data-grid-item]'))];
    const requests = [];

    for (let page = 2; page <= this.sourcePages; page += 1) {
      const url = new URL(window.location.href);
      url.searchParams.delete('items_page');
      url.searchParams.set('page', page);
      requests.push(
        fetch(url.toString())
          .then((response) => response.text())
          .then((html) => {
            const sourceGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
            return sourceGrid ? Array.from(sourceGrid.querySelectorAll(':scope > [data-grid-item]')) : [];
          })
      );
    }

    try {
      pages.push(...(await Promise.all(requests)));
    } catch (error) {
      console.warn('Unable to load all collection variant cards.', error);
    }

    this.items = pages.flat();
    this.totalPages = Math.max(1, Math.ceil(this.items.length / this.itemsPerPage));
    this.itemPage = Math.min(this.itemPage, this.totalPages);
    this.render();
  }

  render() {
    const start = (this.itemPage - 1) * this.itemsPerPage;
    const visibleItems = this.items.slice(start, start + this.itemsPerPage);
    this.grid.replaceChildren(...visibleItems);
    this.grid.classList.remove('loading');

    const templates = this.grid.parentElement.querySelector('.collection-grid-editorial-templates');
    if (templates) templates.dataset.currentPage = String(this.itemPage);

    if (typeof window.initProductCardAlt === 'function') window.initProductCardAlt();
    if (typeof window.initCollectionGridEditorialImages === 'function') window.initCollectionGridEditorialImages();
    this.renderPagination();
    this.observeThirdPartyCards();
  }

  observeThirdPartyCards() {
    if (this.cardObserver) return;

    this.cardObserver = new MutationObserver(() => {
      window.clearTimeout(this.enforceTimer);
      this.enforceTimer = window.setTimeout(() => this.enforceItemLimit(), 50);
    });
    this.cardObserver.observe(this.grid, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-b4b-color']
    });
    this.enforceItemLimit();
  }

  enforceItemLimit() {
    // B4B Variable Products runs after the collection grid initializes and
    // appends its own duplicate color cards. This grid already renders those
    // colors in Liquid, so remove only the app's temporary duplicates.
    this.grid.querySelectorAll(':scope > .variable-tmp').forEach((item) => item.remove());

    const cards = Array.from(this.grid.querySelectorAll(':scope > [data-grid-item]'));
    cards.slice(this.itemsPerPage).forEach((item) => item.remove());

    cards.slice(0, this.itemsPerPage).forEach((item) => {
      const color = item.dataset.b4bColor || item.dataset.cardColor;
      const card = item.querySelector('.product-card-alt');
      if (color && card && typeof window.setProductCardAltColor === 'function') {
        window.setProductCardAltColor(card, color);
      }
      this.syncThirdPartyCardAvailability(item);
    });
  }

  syncThirdPartyCardAvailability(item) {
    // B4B Variable Products clones the source card for each variant but does
    // not update the source card's availability badge or sold-out price state.
    if (!item.classList.contains('variable-products')) return;

    const card = item.querySelector('.product-card-alt');
    if (!card) return;

    const isUnavailable = item.dataset.unavailable === 'true';
    const imageWrapper = card.querySelector('.product-card-alt__image-wrapper');
    const price = card.querySelector('.product-card-alt__price');
    const isComingSoon = card.dataset.comingSoon === 'true';
    let comingSoonBadge = card.querySelector('.product-card-status-badge--coming-soon');
    let notifyButton = card.querySelector('[data-klaviyo-notify-me]');
    let soldOutLabel = price?.querySelector('.product-card-alt__sold-out');

    if (!isUnavailable) {
      notifyButton?.remove();
      comingSoonBadge?.remove();
      soldOutLabel?.remove();
      price?.classList.remove('is-unavailable');
      return;
    }

    if (isComingSoon) {
      notifyButton?.remove();
      soldOutLabel?.remove();
      price?.classList.remove('is-unavailable');
      if (!comingSoonBadge && imageWrapper) {
        comingSoonBadge = document.createElement('span');
        comingSoonBadge.className = 'product-card-status-badge product-card-status-badge--coming-soon';
        comingSoonBadge.textContent = 'Coming Soon';
        imageWrapper.prepend(comingSoonBadge);
      }
      return;
    }

    const variantId = item.dataset.variantId;
    const productLink = card.querySelector('.product-card-alt__title a, .product-card-alt__link');
    const productUrl = productLink?.getAttribute('href');
    const productTitle = card.querySelector('.product-card-alt__title')?.textContent.trim() || 'this product';

    if (!notifyButton && imageWrapper) {
      notifyButton = document.createElement('button');
      notifyButton.type = 'button';
      notifyButton.className = 'product-card-status-badge product-card-status-badge--notify';
      notifyButton.dataset.klaviyoNotifyMe = '';
      notifyButton.textContent = 'Notify Me';
      imageWrapper.prepend(notifyButton);
    }

    if (notifyButton) {
      if (variantId) notifyButton.dataset.variantId = variantId;
      if (productUrl) notifyButton.dataset.productUrl = productUrl;
      notifyButton.setAttribute('aria-label', `Notify me when ${productTitle} is available`);
    }

    if (price) {
      price.classList.add('is-unavailable');
      if (!soldOutLabel) {
        soldOutLabel = document.createElement('span');
        soldOutLabel.className = 'product-card-alt__sold-out';
        soldOutLabel.textContent = 'Sold out';
        price.prepend(soldOutLabel);
      }
    }
  }

  pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    if (page === 1) url.searchParams.delete('items_page');
    else url.searchParams.set('items_page', page);
    return `${url.pathname}${url.search}${url.hash}`;
  }

  renderPagination() {
    if (!this.paginationContainer) return;
    this.paginationContainer.replaceChildren();
    if (this.totalPages <= 1) return;

    const pagination = document.createElement('nav');
    pagination.className = 'pagination collection-variant-pagination';
    pagination.setAttribute('aria-label', 'Pagination');
    const links = document.createElement('div');
    links.className = 'page-numbers nav-links';

    if (this.itemPage > 1) {
      links.append(this.createLink(this.itemPage - 1, 'Previous', 'prev'));
    } else {
      links.append(this.createDisabledLabel('Previous', 'prev'));
    }
    for (let page = 1; page <= this.totalPages; page += 1) {
      if (page === this.itemPage) {
        const current = document.createElement('span');
        current.className = 'page current';
        current.setAttribute('aria-current', 'page');
        current.textContent = page;
        links.append(current);
      } else {
        links.append(this.createLink(page, String(page), 'page'));
      }
    }
    if (this.itemPage < this.totalPages) links.append(this.createLink(this.itemPage + 1, 'Next', 'next'));

    pagination.append(links);
    this.paginationContainer.append(pagination);
  }

  createLink(page, label, className) {
    const wrapper = document.createElement('span');
    wrapper.className = className;
    const link = document.createElement('a');
    link.href = this.pageUrl(page);
    link.textContent = label;
    wrapper.append(link);
    return wrapper;
  }

  createDisabledLabel(label, className) {
    const wrapper = document.createElement('span');
    wrapper.className = `${className} disabled`;
    wrapper.setAttribute('aria-disabled', 'true');
    const text = document.createElement('span');
    text.textContent = label;
    wrapper.append(text);
    return wrapper;
  }
}

function initCollectionGridVariantPagination() {
  document.querySelectorAll('[data-variant-card-pagination="true"]').forEach((grid) => {
    if (grid.dataset.variantPaginationInitialized === 'true') return;
    grid.dataset.variantPaginationInitialized = 'true';
    new CollectionGridVariantPagination(grid);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionGridVariantPagination);
} else {
  initCollectionGridVariantPagination();
}

window.initCollectionGridVariantPagination = initCollectionGridVariantPagination;
